/* eslint-disable react/no-unknown-property */

import { ComponentResource } from '@/api/serverApi'
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedComponent } from '@/stores/active-design'
import { PropertyTabs, setCurrentPropertiesTab } from '@/stores/selected-properties-tab'
import { useMemo, useState } from 'react'
import { PMREMGenerator, Vector3 } from 'three'
import { RootState } from '@/stores'
import { ThreeColors } from '@/helpers/threeColors'
import { useThree } from '@react-three/fiber'
import TBoltForceDisplay from '../../TBoltForceDisplay'
import useComponentParent from '@/hooks/useComponentParent'
import DistanceArrow3D from '../DistanceArrow3D'
import EdgeDistanceValues from '@/consts/EdgeDistanceValues'

const offsetFromBoltEnd = 2

const distanceArrowOffsetFromEdge = 0.5
const verticalOffsetFromBoltHead = 12
const horizontalOffsetFromBoltHead = 150
const referenceOffsetFromBoltHead = 165

type DistancePointType = {
  label: string
  distanceArrowStartPoint: [number, number, number],
  distanceArrowEndPoint: [number, number, number],
  startReferenceLinePoints: [[number, number, number], [number, number, number]],
  endReferenceLinePoints: [[number, number, number], [number, number, number]],
}

type PropTypes = {
  component: ComponentResource
}

const TBoltModel = ({ component }: PropTypes) => {
  const dispatch = useDispatch()
  const three = useThree()

  const productVariants = useSelector((state: RootState) => state.products.productVariants)
  const components = useSelector((state: RootState) => state.activeDesign.design?.components)
  const selectedCameraControlType = useSelector((state: RootState) => state.selectedCameraControlType.option)

  const tBoltVariant = productVariants?.find(p => p.id === component.product_variant_id)

  const { parent: channel } = useComponentParent({ component })
  const { parent: slab } = useComponentParent({ component: channel })

  // const { siblings } = useComponentSiblings({ component })
  const siblings = components?.filter(c => c.parent_id === component.parent_id)

  const channelVariant = productVariants?.find(p => p.id === channel?.product_variant_id)

  const isSelected = useSelector((state: RootState) => state.activeDesign.selectedComponentId  === component.id)

  const [isHovered, setIsHovered] = useState(false)

  const nearestTBolts = useMemo(() => {
    return siblings?.filter(c => Object.prototype.hasOwnProperty.call(c, 't_bolt')).sort((a, b) => {
      const aDistance = Math.abs(a.position?.x ?? 0)
      const bDistance = Math.abs(b.position?.x ?? 0)

      return aDistance - bDistance
    })
  }, [siblings])

  const geometry = useMemo(() => {
    const head = (
      <boxGeometry
        args={[
          parseInt(component.t_bolt?.diameter.replace('M', '') ?? '2'),
          tBoltVariant?.t_bolt?.lip_height ?? 10,
          channelVariant?.cast_in_anchor_channel?.width ?? 10,
        ]}
      />
    )

    const body = (
      <cylinderGeometry
        args={[
          parseInt(component.t_bolt?.diameter.replace('M', '') ?? '2') / 2,
          parseInt(component.t_bolt?.diameter.replace('M', '') ?? '2') / 2,
          component.t_bolt?.length ?? 0,
        ]}
      />
    )

    return {
      head,
      body,
    }
  }
  , [component, tBoltVariant, channelVariant])

  const boltPosition = useMemo(() => {
    const details = slab?.concrete_slab

    if (!details) {
      return new Vector3(0, 0, 0)
    }

    const isEdgeDistanceXPosInfinite = !!(details.is_edge_distance_x_pos_infinite ?? false)
    const isEdgeDistanceXNegInfinite = !!(details.is_edge_distance_x_neg_infinite ?? false)
    const isEdgeDistanceYPosInfinite = !!(details.is_edge_distance_y_pos_infinite ?? false)
    const isEdgeDistanceYNegInfinite = !!(details.is_edge_distance_y_neg_infinite ?? false)

    const xPosEdgeDistance = isEdgeDistanceXPosInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_x_pos
    const xNegEdgeDistance = isEdgeDistanceXNegInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_x_neg
    const yPosEdgeDistance = isEdgeDistanceYPosInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_y_pos
    const yNegEdgeDistance = isEdgeDistanceYNegInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_y_neg

    const channelLength = channel?.cast_in_anchor_channel?.custom_length ?? channel?.cast_in_anchor_channel?.length ?? 0
    const channelDepth = channelVariant?.cast_in_anchor_channel?.depth ?? 0

    const halfChannelWidth = (channelVariant?.cast_in_anchor_channel?.width ?? 0) / 2
    const halfSlabThickness = details.thickness / 2
    const halfSlabHeight = (yPosEdgeDistance + yNegEdgeDistance + (channelVariant?.cast_in_anchor_channel?.width ?? 0)) / 2
    const halfSlabWidth = (xPosEdgeDistance + xNegEdgeDistance + channelLength) / 2
    const halfChannelLength = channelLength / 2
    const halfChannelDepth = channelDepth / 2

    const xOffset = (halfSlabWidth - halfChannelLength - xPosEdgeDistance)
    const zOffset = (halfSlabHeight - halfChannelWidth - yNegEdgeDistance)

    return new Vector3(
      (xOffset - halfChannelLength) + (component?.position?.x ?? 0),
      ((component.t_bolt?.length ?? 0) / 2) + halfSlabThickness - halfChannelDepth,
      zOffset ?? 0,
    )
  }, [component, channel, channelVariant, slab])

  const boltForcePosition = useMemo(() => {
    return new Vector3(
      0,
      ((component.t_bolt?.length ?? 0) / 2) + offsetFromBoltEnd,
      0,
    )
  }, [component])

  const boltHeadPosition = useMemo(() => {
    return new Vector3(
      0,
      -((component.t_bolt?.length ?? 0) / 2),
      0,
    )
  }, [component])

  const channelDistancePositions: DistancePointType | null = useMemo(() => {
    let spacingBetweenNearestBoltDisplays = 44

    const currentTBoltIndex = nearestTBolts?.findIndex(b => b.id === component.id)
    const currentTBolt = nearestTBolts![currentTBoltIndex!]

    const halfChannelWidth = (channelVariant?.cast_in_anchor_channel?.width ?? 0) / 2

    if (currentTBoltIndex !== 0) {
      return null
    }

    const distance = (currentTBolt.position?.x ?? 0)

    const offsetOrigin = boltHeadPosition.x + distanceArrowOffsetFromEdge

    // Cannot define the mathematical reason for a - here, but it works
    const offsetDistance = -(distance - distanceArrowOffsetFromEdge)

    const distanceArrowStartPoint = [
      offsetOrigin,
      boltHeadPosition.y + verticalOffsetFromBoltHead,
      boltHeadPosition.z + horizontalOffsetFromBoltHead + spacingBetweenNearestBoltDisplays,
    ]

    const distanceArrowEndPoint = [
      offsetDistance,
      boltHeadPosition.y + verticalOffsetFromBoltHead,
      boltHeadPosition.z + horizontalOffsetFromBoltHead + spacingBetweenNearestBoltDisplays,
    ]

    const startReferenceLinePoints = [
      [
        offsetOrigin,
        boltHeadPosition.y + verticalOffsetFromBoltHead,
        boltHeadPosition.z,
      ],
      [
        offsetOrigin,
        boltHeadPosition.y + verticalOffsetFromBoltHead,
        boltHeadPosition.z + referenceOffsetFromBoltHead + spacingBetweenNearestBoltDisplays,
      ],
    ]

    const endReferenceLinePoints = [
      [
        offsetDistance,
        boltHeadPosition.y + verticalOffsetFromBoltHead,
        boltHeadPosition.z + halfChannelWidth,
      ],
      [
        offsetDistance,
        boltHeadPosition.y + verticalOffsetFromBoltHead,
        boltHeadPosition.z + referenceOffsetFromBoltHead + spacingBetweenNearestBoltDisplays,
      ],
    ]

    return {
      label: `${Math.abs(distance)}mm`,
      distanceArrowStartPoint: distanceArrowStartPoint as [number, number, number],
      distanceArrowEndPoint: distanceArrowEndPoint as [number, number, number],
      startReferenceLinePoints: startReferenceLinePoints as [[number, number, number], [number, number, number]],
      endReferenceLinePoints: endReferenceLinePoints as [[number, number, number], [number, number, number]],
    }
  }, [nearestTBolts, channelVariant?.cast_in_anchor_channel?.width, boltHeadPosition.x, boltHeadPosition.y, boltHeadPosition.z, component.id])

  const boltDistancePositions: DistancePointType | null = useMemo(() => {
    let spacingBetweenNearestBoltDisplays = 44

    const currentTBoltIndex = nearestTBolts?.findIndex(b => b.id === component.id)
    const nextTBoltIndex = currentTBoltIndex! + 1

    const currentTBolt = nearestTBolts![currentTBoltIndex!]
    const nextTBolt = nearestTBolts![nextTBoltIndex]

    if (nextTBolt === undefined) {
      return null
    }

    const distance = (currentTBolt.position?.x ?? 0) - (nextTBolt.position?.x ?? 0)

    const offsetOrigin = boltHeadPosition.x + distanceArrowOffsetFromEdge

    // Cannot define the mathematical reason for a - here, but it works
    const offsetDistance = -(distance - distanceArrowOffsetFromEdge)

    const distanceArrowStartPoint = [
      offsetOrigin,
      boltHeadPosition.y + verticalOffsetFromBoltHead,
      boltHeadPosition.z + horizontalOffsetFromBoltHead + spacingBetweenNearestBoltDisplays,
    ]

    const distanceArrowEndPoint = [
      offsetDistance,
      boltHeadPosition.y + verticalOffsetFromBoltHead,
      boltHeadPosition.z + horizontalOffsetFromBoltHead + spacingBetweenNearestBoltDisplays,
    ]

    const startReferenceLinePoints = [
      [
        offsetOrigin,
        boltHeadPosition.y + verticalOffsetFromBoltHead,
        boltHeadPosition.z,
      ],
      [
        offsetOrigin,
        boltHeadPosition.y + verticalOffsetFromBoltHead,
        boltHeadPosition.z + referenceOffsetFromBoltHead + spacingBetweenNearestBoltDisplays,
      ],
    ]

    const endReferenceLinePoints = [
      [
        offsetDistance,
        boltHeadPosition.y + verticalOffsetFromBoltHead,
        boltHeadPosition.z,
      ],
      [
        offsetDistance,
        boltHeadPosition.y + verticalOffsetFromBoltHead,
        boltHeadPosition.z + referenceOffsetFromBoltHead + spacingBetweenNearestBoltDisplays,
      ],
    ]

    return {
      label: `${Math.abs(distance)}mm`,
      distanceArrowStartPoint: distanceArrowStartPoint as [number, number, number],
      distanceArrowEndPoint: distanceArrowEndPoint as [number, number, number],
      startReferenceLinePoints: startReferenceLinePoints as [[number, number, number], [number, number, number]],
      endReferenceLinePoints: endReferenceLinePoints as [[number, number, number], [number, number, number]],
    }
  }, [component, nearestTBolts, boltHeadPosition])

  const envMap = useMemo(() => {
    const generator = new PMREMGenerator(three.gl).fromScene(three.scene)
    return generator.texture
  }, [three])

  return (
    <group
      position={boltPosition}
      onClick={(e) => {
        e.stopPropagation()

        if (selectedCameraControlType !== 'cursor') {
          return
        }

        dispatch(setSelectedComponent(component.id))
        dispatch(setCurrentPropertiesTab(PropertyTabs.Selected))
      }}
      onPointerEnter={(e) => {
        e.stopPropagation()

        if (selectedCameraControlType !== 'cursor') {
          return
        }

        setIsHovered(true)
      }}
      onPointerLeave={(e) => {
        e.stopPropagation()

        if (selectedCameraControlType !== 'cursor') {
          return
        }

        setIsHovered(false)
      }}
    >
      {
        isSelected
          && (
            <TBoltForceDisplay
              position={boltForcePosition.toArray() as [number, number, number]}
            />
          )
      }

      {
        boltDistancePositions && (
          <DistanceArrow3D
            points={[boltDistancePositions.distanceArrowStartPoint, boltDistancePositions.distanceArrowEndPoint]}
            referenceLinePoints={[boltDistancePositions.startReferenceLinePoints, boltDistancePositions.endReferenceLinePoints]}
            label={boltDistancePositions.label}
          />
        )
      }

      {
        channelDistancePositions && (
          <DistanceArrow3D
            points={[channelDistancePositions.distanceArrowStartPoint, channelDistancePositions.distanceArrowEndPoint]}
            referenceLinePoints={[channelDistancePositions.startReferenceLinePoints, channelDistancePositions.endReferenceLinePoints]}
            label={channelDistancePositions.label}
          />
        )
      }

      <mesh>
        { geometry.body }
        <meshPhysicalMaterial
          ior={1.52}
          specularIntensity={0.5}
          roughness={0.3}
          metalness={0.4}
          envMap={envMap}
          color={
            isSelected
              ? ThreeColors.selectedColor
              : (
                isHovered
                  ? [0.1, 0.1, 0.1]
                  : [0.15, 0.15, 0.15]
              )
          }
        />
      </mesh>
      <mesh
        position={boltHeadPosition}
      >
        { geometry.head }
        <meshPhysicalMaterial
          ior={1.52}
          specularIntensity={0.5}
          roughness={0.3}
          metalness={0.4}
          envMap={envMap}
          color={
            isSelected
              ? ThreeColors.selectedColor
              : (
                isHovered
                  ? [0.1, 0.1, 0.1]
                  : [0.15, 0.15, 0.15]
              )
          }
        />
      </mesh>
    </group>
  )
}

export default TBoltModel
